import React, { useState, useEffect } from "react";
import { Loader2, Trash } from "lucide-react";
import { Impact } from "util/constants";
import { PrivacyLevel } from "util/userUtils";
import TextEditor from "components/TextEditor";
import EntityPrivacy from "components/Entities/EntityPrivacy";
import api from "config/axiosConfig";
import { useUser } from "util/UserContext";
import { toast } from "sonner";
import { apiRoutes } from "util/apiRoutes";
import AlignmentFormContent from "./AlignmentFormContent";

interface AlignmentFormProps {
  teabate: Impact;
  onSubmitSuccess?: () => void;
  onSaved?: (payload: {
    teabateId: number;
    response: number;
    importance: number;
    note: string;
    privacy: PrivacyLevel;
  }) => void | Promise<void>;
  showTeabateTitle?: boolean;
  initialResponse?: number;
  initialImportance?: number;
  initialNote?: string;
  initialPrivacy?: PrivacyLevel;
  submitButtonText?: string;
  showCancelButton?: boolean;
  onCancel?: () => void;
  editMode?: boolean;
  className?: string;
}

const AlignmentForm: React.FC<AlignmentFormProps> = ({
  teabate,
  onSubmitSuccess,
  onSaved,
  showTeabateTitle = true,
  initialResponse,
  initialImportance,
  initialNote = "",
  initialPrivacy = "public",
  submitButtonText = "Save",
  showCancelButton = false,
  onCancel,
  editMode = false,
  className = "",
}) => {
  const { currentUser } = useUser();
  
  const [response, setResponse] = useState(initialResponse ?? 0);
  const [importance, setImportance] = useState(initialImportance ?? 0);
  const [alignmentData, setAlignmentData] = useState<any | null>(null);
  const [note, setNote] = useState(initialNote);
  const [privacy, setPrivacy] = useState<PrivacyLevel>(initialPrivacy || currentUser?.properties?.privacy_content_default_audience?.value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Fetch existing alignment data
  useEffect(() => {
    const fetchAlignment = async () => {
      if (!currentUser) return;

      try {
        const { data } = await api.get(apiRoutes.user.alignmentByTeabate(teabate.id));
        if (data) {
          setResponse(data.response);
          setImportance(data.importance);
          setPrivacy(data.privacy);
          setAlignmentData(data)
          if (data.comment) {
            setNote(data.comment.content);
          }
        }
      } catch (error) {
        console.error('Error fetching alignment:', error);
        // Keep initial values if fetch fails
      }
    };

    // Only fetch if no initial values provided
    if (initialResponse === undefined && initialImportance === undefined) {
      fetchAlignment();
    }
  }, [currentUser, teabate.id, initialResponse, initialImportance]);

  // Update state when initial props change
  useEffect(() => {
    if (initialResponse !== undefined) setResponse(initialResponse);
  }, [initialResponse]);

  useEffect(() => {
    if (initialImportance !== undefined) setImportance(initialImportance);
  }, [initialImportance]);

  useEffect(() => {
    setNote(initialNote);
  }, [initialNote]);

  useEffect(() => {
    setPrivacy(initialPrivacy);
  }, [initialPrivacy]);

  const handleSubmit = async () => {
    if (!currentUser) return;

    setIsSubmitting(true);
    try {
      await api.post(apiRoutes.user.alignments, {
        teabateId: teabate.id,
        response,
        importance,
        privacy,
        comment: {
          content: note,
          properties: {
            privacy
          }
        }
      });
      
      await onSaved?.({
        teabateId: teabate.id,
        response,
        importance,
        note,
        privacy,
      });
      toast.success(editMode ? 'Alignment updated!' : 'Alignment saved!');
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (error) {
      console.error('Error saving alignment:', error);
      toast.error('Failed to save alignment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!currentUser || !alignmentData) return;

    setIsRemoving(true);
    try {
      await api.delete(apiRoutes.user.alignmentById(alignmentData.id));
      
      toast.success('Alignment removed!');
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (error) {
      console.error('Error removing alignment:', error);
      toast.error('Failed to remove alignment.');
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <AlignmentFormContent
        teabateTitle={teabate.title}
        dimension={typeof teabate.properties?.dimension === "string" ? teabate.properties.dimension : null}
        response={response}
        importance={importance}
        onResponseChange={setResponse}
        onImportanceChange={setImportance}
        showTeabateTitle={showTeabateTitle}
      />
      {currentUser?.properties?.privacy_content_default_audience?.value && (
      <div className="form-control">
        <label className="block text-sm font-semibold mb-2">Privacy</label>
        <EntityPrivacy 
          privacy={editMode ? alignmentData?.privacy : currentUser?.properties?.privacy_content_default_audience?.value} 
          onPrivacyChange={setPrivacy} 
          actorType="user" 
          hideAnonymous={true}
        />
      </div>
      )}

      <div>
        <label className="block text-sm font-semibold mb-1">
          Comment
        </label>
        <TextEditor
          value={note}
          onChange={setNote}
          editorType="entity"
          placeholder="Add a comment about your perspective"
        />
      </div>

      <div className="flex justify-between gap-3 pt-2">
        {editMode && alignmentData && (
          <button
            type="button"
            onClick={handleRemove}
            className="btn-secondary flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            disabled={isRemoving || isSubmitting}
          >
            {isRemoving ? <Loader2 className="animate-spin size-4"/> : <><Trash className="size-4"/> Remove</>}
          </button>
        )}
        <div className="flex gap-3 ml-auto">
          {showCancelButton && (
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isSubmitting || isRemoving}
            >
              Cancel
            </button>
          )}
          <button 
            onClick={handleSubmit} 
            disabled={isSubmitting || isRemoving} 
            className="btn-primary"
          >
            {isSubmitting ? <Loader2 className="animate-spin size-4"/> : submitButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlignmentForm;